import type { RecordRow } from './api';
import type { ResolvedSection } from './resumeVersion';

// ===================================================================
// THE PROFILE — THE FACE, AND WHAT EVERY ARRANGING CONTROL SAYS BACK.
// ===================================================================
//
// m19 (the manual's resume frame, 2026-09-02) is a text document with no face
// on it and no sign that any of it can be arranged. Every capability was
// already built — `aa_profile.profile_current_headshot` is the household's
// chosen face, `aa_resume_version.resume_version_headshot` is one
// arrangement's own, and lib/resumeVersion.ts moves sections, turns rows off
// and places new ones — and NONE of it had a control that named itself.
//
// So this file holds the two things that surfacing needs and a screen should
// not invent twice: WHICH MEDIA ARE FACES, and WHAT A CONTROL SAYS BACK.
//
// ⚑ EVERY SENTENCE HERE IS A RECEIPT THAT NAMES THE OBJECT. "Saved" names
// nothing; "Training moved above Television" is a thing a person can check
// against the page they are looking at. The voice canon's rule, and the
// reason these are pure functions with tests rather than template literals
// scattered through JSX.

// -------------------------------------------------------------------
// 1. WHICH MEDIA ARE FACES
// -------------------------------------------------------------------

/**
 * The `aa_media` kinds that are a photograph of a person.
 *
 * ⚠ THIS IS A MIRROR OF THE PACK'S OWN VOCABULARY, and it is the only copy of
 * it on the resume path — guarded by resumeProfile.test.ts, which reads the
 * pack's `aa_media` picklist off recordTypes.ts and refuses a value the pack
 * does not declare (the same discipline packLinkKeys.test.ts holds for the
 * relationship keys).
 *
 * ⚑ AND IT IS A KIND TEST RATHER THAN A FACT TEST, which is the opposite of
 * lib/selfTape.ts's rule, deliberately. That file keys the production
 * treatment on FACTS ("a tape is a tape because it has takes") because a
 * wrong answer there is a row that reads a little too loudly. Here a wrong
 * answer is a VIDEO handed to an <img>: a self-tape attached from an audition
 * carries no takes, no corner and no observation, so every fact-shaped test
 * lets it through and the picker fills with broken tiles. Under-showing is
 * recoverable — the record editor still sets a kind — and a broken picture on
 * the page a person is choosing their face from is not.
 */
export const LOOK_KINDS: readonly string[] = ['headshot', 'photo'];

/** A media record's own kind, or null. */
function kindOf(row: RecordRow): string | null {
  const v = row.fields.kind;
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
}

/**
 * The household's LOOKS — the media a face can be chosen from.
 *
 * A file is required as well as the kind: a record somebody made and never
 * attached a photograph to would render as an empty tile, which reads as a
 * picture that failed to load rather than as a record with nothing in it.
 */
export function looksFrom(media: RecordRow[]): RecordRow[] {
  return media.filter((m) => {
    const kind = kindOf(m);
    if (kind === null || !LOOK_KINDS.includes(kind)) return false;
    const ref = m.fields.original_file_ref;
    return typeof ref === 'string' && ref.trim() !== '';
  });
}

/**
 * What to CALL a look. The household's own label when there is one, the
 * record's display name otherwise — `versionName`'s rule one file over,
 * applied to the other half of the same feature.
 *
 * The label is preferred over the display name on purpose: `aa_media`'s
 * name template is `{kind} — {label}`, so a receipt built from the display
 * name reads "Headshot — Stage look prints on Theatre 2026", which says the
 * word "headshot" to somebody who is looking at a row of headshots.
 */
export function lookName(row: RecordRow): string {
  const label = row.fields.label;
  if (typeof label === 'string' && label.trim() !== '') return label.trim();
  return row.display_name;
}

// -------------------------------------------------------------------
// 2. WHAT A CONTROL SAYS BACK
// -------------------------------------------------------------------

/**
 * A section moved, named against the section it changed places with.
 *
 * ⚑ "ABOVE TELEVISION", NOT "UP". A person who has just pressed ↑ knows they
 * pressed ↑; what they cannot see without scrolling a long sheet is what the
 * section landed next to.
 *
 * ⚑ THE LABELS ARE THE ONES ON SCREEN, BEFORE the move. `resolveVersion`
 * DROPS a section whose records have all gone, so the stored order and the
 * one a person is looking at can differ by any number of entries — and a
 * receipt that named a section they cannot see would be worse than none.
 * This applies the move to the visible list itself and reads the neighbour
 * off the result, which is exactly what they will see.
 *
 * ⚠ NULL WHEN NOTHING MOVED. A move off either end is a no-op in
 * `moveSection` — the screen's buttons are disabled there, but a receipt for
 * a change that did not happen is a lie in the one register that must never
 * tell one, and a disabled button is a hint rather than a guarantee.
 */
export function movedReceipt(
  orderBefore: readonly string[],
  label: string,
  delta: -1 | 1
): string | null {
  const index = orderBefore.indexOf(label);
  const to = index + delta;
  if (index < 0 || to < 0 || to >= orderBefore.length) return null;
  const after = [...orderBefore];
  const [moved] = after.splice(index, 1);
  after.splice(to, 0, moved!);
  const neighbour = after[to - delta];
  if (neighbour === undefined) return null;
  return `${label} moved ${delta === -1 ? 'above' : 'below'} ${neighbour}`;
}

/** A row turned off, or back on. Named by the row's own lead — the
 * production, or the school — because that is the word the person is
 * looking at on the line they just tapped. */
export function toggledReceipt(lead: string, nowExcluded: boolean): string {
  return nowExcluded
    ? `${lead} left off this arrangement`
    : `${lead} is back on this arrangement`;
}

/** An unplaced row put on the sheet, named with the section it landed in —
 * `placeRow` APPENDS a section the layout has never held, so "placed under
 * Training" is sometimes the first time that heading has existed. */
export function placedReceipt(lead: string, sectionLabel: string): string {
  return `${lead} placed under ${sectionLabel}`;
}

/**
 * A face chosen. `on` is the document it prints on — an arrangement's name,
 * or the household's own resume.
 *
 * ⚑ IT SAYS "PRINTS", not "selected". The whole reason a person is choosing
 * here is the page that leaves the house, and "Stage look selected" is a
 * statement about this app's state where "Stage look prints on Theatre 2026"
 * is a statement about the document.
 */
export function faceReceipt(look: string, on: string): string {
  return `${look} prints on ${on}`;
}

/** A face taken off a document. The arrangement then prints the household's
 * chosen face — never "no photo" — so the sentence says which. */
export function faceClearedReceipt(on: string): string {
  return `${on} prints the face this household chose`;
}

/** An arrangement saved, named. */
export function savedReceipt(versionLabel: string): string {
  return `${versionLabel} saved`;
}

// -------------------------------------------------------------------
// 3. WHAT IS ACTUALLY ON THE SHEET
// -------------------------------------------------------------------

/**
 * `printableSections` said out loud: the headings that will print and how
 * many lines they carry between them.
 *
 * ⚑ THE COUNT IS OF LINES, NOT OF SECTIONS, because the thing a person is
 * managing here is the length of one page. Two sections and nineteen lines is
 * a two-page resume; five sections and six lines is not.
 *
 * Empty is its own sentence rather than a zero — a sheet with nothing on it
 * is a state, not a number.
 */
export function printsLine(printable: readonly ResolvedSection[]): string {
  const lines = printable.reduce((n, s) => n + s.rows.length, 0);
  if (lines === 0) return 'Nothing prints on this arrangement yet';
  const labels = printable.map((s) => s.label).join(' · ');
  return `${labels} — ${lines} ${lines === 1 ? 'line' : 'lines'}`;
}
